// How the console shows stored values to a person (24 DES-561 items 4 and 10; decision record 0071).
// Display only: what is stored, sent or checked is never changed.

/** The value with every fraction shown to at most `places` decimal places (3 by default). */
export function tidyNumbers(v: unknown, places = 3): unknown {
  if (typeof v === 'number') {
    if (Number.isInteger(v) || !Number.isFinite(v)) return v;
    const f = 10 ** places;
    return Math.round(v * f) / f;
  }
  if (Array.isArray(v)) return v.map((x) => tidyNumbers(x, places));
  if (v && typeof v === 'object')
    return Object.fromEntries(
      Object.entries(v as Record<string, unknown>).map(([k, x]) => [k, tidyNumbers(x, places)]),
    );
  return v;
}

/** A stored value as indented JSON, its fractions tidied. */
export const jsonText = (v: unknown): string => JSON.stringify(tidyNumbers(v), null, 2);

/** A stored code as words: "deep_rough" reads "deep rough". Anything else is shown as it is. */
export const words = (v: unknown): string =>
  v === null || v === undefined ? '—' : String(v).replace(/_/g, ' ');

/** The same, with a capital letter first: "every_shot" reads "Every shot". */
export const capitalWords = (v: unknown): string => {
  const w = words(v);
  return w === '—' ? w : w.charAt(0).toUpperCase() + w.slice(1);
};

const nf = (places: number) =>
  new Intl.NumberFormat('en-GB', { minimumFractionDigits: places, maximumFractionDigits: places });

/** A number to a fixed number of decimal places, or a dash. */
export const fixed = (v: number | null | undefined, places: number): string =>
  typeof v === 'number' && Number.isFinite(v) ? nf(places).format(v) : '—';

/** A no-break space: the two things either side of it stay on one line. */
export const NBSP = String.fromCharCode(0xa0);

/** Where a setting's name may break: after each dot, underscore and comma. */
export const NAME_BREAKS = /(?<=[._,])/;
/** Where a value may break: after each comma, never inside a number or a word. */
export const VALUE_BREAKS = /(?<=,)/;
/** Where a file path may break: after each slash. */
export const PATH_BREAKS = /(?<=\/)/;

/** The pieces of a long name, value or path, split where it may break (a name's by default). */
export const breakPoints = (text: string, at: RegExp = NAME_BREAKS): string[] => text.split(at);
